import { useState } from 'react';
import { X } from 'lucide-react';
import CommercialTermsFields, {
  termsPayload,
  type CommercialTerms,
} from './CommercialTermsFields';
import type { Client } from '../types';

/**
 * Correcting a client that already exists.
 *
 * Until this there was no way to change one at all. The tax fields, the
 * escalation point and the GSTIN were only on the create form, and every
 * client in the live data arrived through the importer instead — so the GST
 * and TDS rates invoicing depends on could not be set on a single real client,
 * and every "Ready to invoice" row read "Tax on file: None set" with no way to
 * fix it.
 *
 * Name is shown and locked, with the reason on screen. Renaming is what the
 * merge tool does; a quiet rename here would leave every alias pointing at the
 * old spelling, and a greyed box with no explanation reads as a bug.
 *
 * The server refuses this to anyone below company admin regardless of who can
 * see the button. Hiding the control is courtesy; the 403 behind it is the
 * rule.
 */
export default function ClientEditDialog({
  client,
  saving,
  onCancel,
  onSave,
}: {
  client: Client;
  saving: boolean;
  onCancel: () => void;
  onSave: (payload: Record<string, unknown>) => void;
}) {
  const [gstin, setGstin] = useState(client.gstin ?? '');
  const [gstRate, setGstRate] = useState(client.gstRate ?? '');
  const [tdsRate, setTdsRate] = useState(client.tdsRate ?? '');
  const [tdsSection, setTdsSection] = useState(client.tdsSection ?? '');
  const [city, setCity] = useState(client.city ?? '');
  const [contactName, setContactName] = useState(client.contactName ?? '');
  const [contactEmail, setContactEmail] = useState(client.contactEmail ?? '');
  const [contactPhone, setContactPhone] = useState(client.contactPhone ?? '');
  // Seeded from the row so the dialog opens showing what was agreed. An
  // unset basis stays unset — the dialog must not turn "no standing terms"
  // into a choice nobody made just by being opened and saved.
  const [terms, setTerms] = useState<CommercialTerms>({
    feeBasis: client.defaultFeeBasis ?? '',
    feePercent: client.defaultFeePercent ?? '',
    flatAmount: client.defaultBillingAmount ?? '',
    paymentTerms: String(client.paymentTermsDays),
    replacementDays:
      client.defaultReplacementDays === null
      || client.defaultReplacementDays === undefined
        ? ''
        : String(client.defaultReplacementDays),
  });
  const [website, setWebsite] = useState(client.website ?? '');
  const [escalationName, setEscalationName] = useState(client.escalationName ?? '');
  const [escalationEmail, setEscalationEmail] = useState(client.escalationEmail ?? '');
  const [escalationPhone, setEscalationPhone] = useState(client.escalationPhone ?? '');

  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/30 p-4">
      <form
        className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-xl bg-white shadow-xl"
        onSubmit={(e) => {
          e.preventDefault();
          onSave({
            gstin: gstin.trim() || null,
            // Null, not zero, when blank. The server reads null as "not
            // established" and applies nothing; zero would read as a decision.
            gstRate: gstRate.trim() || null,
            tdsRate: tdsRate.trim() || null,
            tdsSection: tdsSection.trim() || null,
            city: city.trim() || null,
            contactName: contactName.trim() || null,
            contactEmail: contactEmail.trim() || null,
            contactPhone: contactPhone.trim() || null,
            ...termsPayload(terms),
            website: website.trim() || null,
            escalationName: escalationName.trim() || null,
            escalationEmail: escalationEmail.trim() || null,
            escalationPhone: escalationPhone.trim() || null,
          });
        }}
      >
        <div className="flex items-start justify-between gap-3 border-b border-gray-200 px-5 py-4">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">
              Edit {client.name}
            </h3>
            <p className="mt-0.5 text-xs text-gray-500">
              The name is fixed here — changing it is what merging two clients
              does, so that it stays attached to the spellings the importer
              already matched. Use the data-quality panel to merge.
            </p>
          </div>
          <button type="button" className="btn-ghost" onClick={onCancel} aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-4">
          {/* Agreed once with the client, suggested on every placement.
              Same control set the add form uses, so the two cannot drift. */}
          <section>
            <h4 className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">
              Commercial Terms
            </h4>
            <CommercialTermsFields value={terms} onChange={setTerms} />
          </section>

          <section>
            <h4 className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">
              Tax Treatment
            </h4>
            <div>
              <label className="label" htmlFor="ce-gstin">GSTIN</label>
              <input
                spellCheck={false}
                id="ce-gstin"
                className="input font-mono uppercase"
                maxLength={15}
                value={gstin}
                onChange={(e) => setGstin(e.target.value)}
                placeholder="36AABCT1234K1Z5"
              />
              <p className="mt-1 text-xs text-gray-500">
                {/* Two different things wearing similar names, and confusing
                    them puts the wrong one on an invoice. */}
                Their registration number, printed on their invoices. Not the
                same as the GST rate below. Fifteen characters.
              </p>
            </div>

            <div className="mt-3 grid grid-cols-2 gap-2">
              <div>
                <label className="label" htmlFor="ce-gst">GST %</label>
                <input
                  spellCheck={false}
                  id="ce-gst"
                  className="input"
                  inputMode="decimal"
                  value={gstRate}
                  onChange={(e) => setGstRate(e.target.value)}
                  placeholder="18"
                />
              </div>
              <div>
                <label className="label" htmlFor="ce-tds">TDS %</label>
                <input
                  spellCheck={false}
                  id="ce-tds"
                  className="input"
                  inputMode="decimal"
                  value={tdsRate}
                  onChange={(e) => setTdsRate(e.target.value)}
                  placeholder="10"
                />
              </div>
            </div>

            <label className="label mt-3" htmlFor="ce-section">TDS Section</label>
            <input
              spellCheck={false}
              id="ce-section"
              className="input"
              maxLength={20}
              value={tdsSection}
              onChange={(e) => setTdsSection(e.target.value)}
              placeholder="194J"
            />

            <p className="mt-2 text-[11px] text-gray-500">
              Left blank, no tax figure appears on this client&rsquo;s
              invoices. TDS is calculated on the amount before GST and is
              withheld by the client, not by us. These figures have not been
              reviewed by a chartered accountant.
            </p>
          </section>

          <section>
            <h4 className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">
              Contact
            </h4>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="label" htmlFor="ce-city">Client Address (Head Office Or Billing)</label>
                <input
                  spellCheck={false}
                  id="ce-city"
                  className="input"
                  maxLength={100}
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                />
              </div>
              <div>
                <label className="label" htmlFor="ce-contact">Client Contact Name</label>
                <input
                  spellCheck={false}
                  id="ce-contact"
                  className="input"
                  maxLength={160}
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                />
              </div>
              <div>
                <label className="label" htmlFor="ce-email">Contact Email</label>
                <input
                  spellCheck={false}
                  id="ce-email"
                  type="email"
                  className="input"
                  maxLength={255}
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                />
              </div>
              <div>
                <label className="label" htmlFor="ce-phone">Contact Phone</label>
                <input
                  spellCheck={false}
                  id="ce-phone"
                  className="input"
                  inputMode="tel"
                  maxLength={20}
                  value={contactPhone}
                  onChange={(e) => setContactPhone(e.target.value)}
                />
              </div>
              <div>
                <label className="label" htmlFor="ce-web">Website</label>
                <input
                  spellCheck={false}
                  id="ce-web"
                  type="url"
                  className="input"
                  maxLength={255}
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                />
              </div>
            </div>
          </section>

          <section>
            <h4 className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">
              Escalation Point
            </h4>
            <div className="grid gap-3 sm:grid-cols-3">
              <div>
                <label className="label" htmlFor="ce-esc-name">Name</label>
                <input
                  spellCheck={false}
                  id="ce-esc-name"
                  className="input"
                  maxLength={160}
                  value={escalationName}
                  onChange={(e) => setEscalationName(e.target.value)}
                />
              </div>
              <div>
                <label className="label" htmlFor="ce-esc-email">Email</label>
                <input
                  spellCheck={false}
                  id="ce-esc-email"
                  type="email"
                  className="input"
                  maxLength={255}
                  value={escalationEmail}
                  onChange={(e) => setEscalationEmail(e.target.value)}
                />
              </div>
              <div>
                <label className="label" htmlFor="ce-esc-phone">Phone</label>
                <input
                  spellCheck={false}
                  id="ce-esc-phone"
                  className="input"
                  inputMode="tel"
                  maxLength={20}
                  value={escalationPhone}
                  onChange={(e) => setEscalationPhone(e.target.value)}
                />
              </div>
            </div>
          </section>
        </div>

        <div className="flex justify-end gap-2 border-t border-gray-200 px-5 py-3">
          <button type="button" className="btn-ghost" onClick={onCancel}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={saving}>
            Save changes
          </button>
        </div>
      </form>
    </div>
  );
}
